import {
  DirectionalLight,
  GizmoManager,
  LightGizmo,
  Scene,
} from '@babylonjs/core';

import {
  AdvancedDynamicTexture,
  Slider,
  Rectangle,
  TextBlock,
  Control,
} from '@babylonjs/gui';

export class SceneLightsEditUtil {

  private static readonly SliderPanelWidth = 220;
  private static readonly SliderPanelHeight = 50;
  private static readonly SliderPanelGap = 8;
  private static readonly SliderPanelMargin = 10;

  private static getTopSliderTopOffset(index: number): number {
    return SceneLightsEditUtil.SliderPanelMargin
      + index * (
        SceneLightsEditUtil.SliderPanelHeight
        + SceneLightsEditUtil.SliderPanelGap
      );
  }

  private static CreateIntensitySliderPanel(
    uiAdvancedTexture: AdvancedDynamicTexture,
    index: number,
    title: string,
    value: number,
    maxValue: number,
    onValueChanged: (value: number) => void,
  ): void {

    const panel = new Rectangle();
    panel.width = `${SceneLightsEditUtil.SliderPanelWidth}px`;
    panel.height = `${SceneLightsEditUtil.SliderPanelHeight}px`;
    panel.cornerRadius = 8;
    panel.color = '#666666';
    panel.thickness = 1;
    panel.background = '#AAAAAA99';

    panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    panel.left = `${SceneLightsEditUtil.SliderPanelMargin}px`;
    panel.top = `${SceneLightsEditUtil.getTopSliderTopOffset(index)}px`;

    uiAdvancedTexture.addControl(panel);

    const label = new TextBlock();
    label.fontSize = 14;
    label.color = '#111111';
    label.text = `${title}: ${value.toFixed(1)}`;
    label.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    label.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    label.top = -5;
    panel.addControl(label);

    const slider = new Slider();
    slider.minimum = 0.0;
    slider.maximum = maxValue;
    slider.value = value;
    slider.height = '15px';
    slider.width = '210px';
    slider.color = '#003399';
    slider.background = 'grey';
    slider.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    slider.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    panel.addControl(slider);

    slider.onValueChangedObservable.add((newValue) => {
      label.text = `${title}: ${newValue.toFixed(1)}`;
      onValueChanged(newValue);
    });
  }

  private static CreateLightGizmoLabel(
    lightGizmo: LightGizmo,
    uiAdvancedTexture: AdvancedDynamicTexture,
    lightIndex: number,
  ): void {

    const panel = new Rectangle();
    panel.width = '40px';
    panel.height = '24px';
    panel.cornerRadius = 8;
    panel.color = 'LightBlue';
    panel.thickness = 1;
    panel.background = '#AAAAAA99';

    uiAdvancedTexture.addControl(panel);

    panel.linkWithMesh(lightGizmo.attachedMesh);
    panel.linkOffsetY = 80;

    const label = new TextBlock();
    label.fontSize = 14;
    label.color = '#111111';
    label.text = `L${lightIndex}`;

    panel.addControl(label);
  }

  public static CreateLightEditControl(
    light: DirectionalLight,
    lightIndex: number,
    scene: Scene,
    uiAdvancedTexture: AdvancedDynamicTexture,
    invalidateRender?: () => void,
  ): void {

    const lightGizmo = new LightGizmo();
    lightGizmo.light = light;

    const gizmoManager = new GizmoManager(scene);
    gizmoManager.positionGizmoEnabled = true;
    gizmoManager.rotationGizmoEnabled = true;
    gizmoManager.boundingBoxGizmoEnabled = true;
    gizmoManager.usePointerToAttachGizmos = false;
    gizmoManager.attachToMesh(lightGizmo.attachedMesh);

    SceneLightsEditUtil.CreateLightGizmoLabel(
      lightGizmo,
      uiAdvancedTexture,
      lightIndex,
    );

    SceneLightsEditUtil.CreateIntensitySliderPanel(
      uiAdvancedTexture,
      lightIndex,
      `Light ${lightIndex}`,
      light.intensity,
      8,
      (value) => {
        // eslint-disable-next-line no-param-reassign
        light.intensity = value;
        invalidateRender?.();
      },
    );
  }

  public static CreateEnvironmentLightControl(
    scene: Scene,
    uiAdvancedTexture: AdvancedDynamicTexture,
    invalidateRender?: () => void,
  ): void {

    SceneLightsEditUtil.CreateIntensitySliderPanel(
      uiAdvancedTexture,
      0,
      'Environment',
      scene.environmentIntensity,
      3,
      (value) => {
        // eslint-disable-next-line no-param-reassign
        scene.environmentIntensity = value;
        invalidateRender?.();
      },
    );
  }
}
